<template>
    <div class="payout-history">
        <!-- YTD Summary Header -->
        <div class="mb-5 bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div class="p-5 border-b border-gray-100">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 class="text-lg font-semibold text-gray-900">Payouts</h1>
                        <p class="text-sm text-gray-500 mt-1">
                            {{ currentYear }} Year to Date:
                            <span class="font-semibold text-mint-dark tabular-nums">${{ ytdTotal }}</span>
                        </p>
                    </div>
                    <button
                        @click="exportToCsv"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                        :disabled="loading"
                    >
                        <svg class="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV
                    </button>
                </div>
            </div>

            <!-- Year Tabs -->
            <div class="flex overflow-x-auto border-b border-gray-100" v-if="availableYears.length > 0">
                <button
                    v-for="year in availableYears"
                    :key="year"
                    @click="selectYear(year)"
                    class="flex-shrink-0 px-5 py-3 text-sm font-medium transition-colors relative"
                    :class="selectedYear === year
                        ? 'text-logo border-b-2 border-logo bg-logo/5'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'"
                >
                    {{ year }}
                    <span class="ml-2 text-xs tabular-nums" :class="selectedYear === year ? 'text-logo' : 'text-gray-400'">
                        ${{ formatAmount(yearlyTotals[year] || 0) }}
                    </span>
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading && !hasData" class="text-center py-16">
            <svg class="animate-spin h-6 w-6 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="mt-3 text-sm text-gray-400">Loading payouts...</p>
        </div>

        <!-- Monthly Breakdown -->
        <div v-else-if="hasData" class="space-y-3">
            <div
                v-for="month in sortedMonths"
                :key="month.number"
                class="bg-white rounded-2xl border border-gray-100 overflow-hidden"
            >
                <!-- Month Header -->
                <div
                    @click="toggleMonth(month.number)"
                    class="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                    <div class="flex items-center gap-3">
                        <span class="text-base font-medium text-gray-900">{{ month.name }}</span>
                        <span class="text-xs text-gray-400">{{ getMonthPayoutCount(month.number) }} payouts</span>
                    </div>
                    <div class="flex items-center gap-3">
                        <span class="text-base font-semibold text-gray-900 tabular-nums">${{ formatAmount(monthlyTotals[month.number] || 0) }}</span>
                        <svg
                            class="w-4 h-4 text-gray-300 transition-transform"
                            :class="{ 'rotate-180': expandedMonths[month.number] }"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                <!-- Month Content - Date Groups -->
                <transition name="expand">
                    <div v-if="expandedMonths[month.number]" class="border-t border-gray-100">
                        <div
                            v-for="dateGroup in getDateGroupsForMonth(month.number)"
                            :key="dateGroup.date"
                            class="border-b border-gray-50 last:border-b-0"
                        >
                            <!-- Date Header -->
                            <div
                                @click="toggleDate(dateGroup.date)"
                                class="flex justify-between items-center px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors bg-gray-50/30"
                            >
                                <div class="flex items-center gap-2">
                                    <span class="w-2 h-2 rounded-full bg-mint"></span>
                                    <span class="text-sm font-medium text-gray-700">{{ formatDate(dateGroup.date) }}</span>
                                    <span class="text-xs text-gray-400">{{ dateGroup.payouts.length }} item{{ dateGroup.payouts.length > 1 ? 's' : '' }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="text-sm font-medium text-gray-900 tabular-nums">${{ formatAmount(dateGroup.total) }}</span>
                                    <svg
                                        class="w-3 h-3 text-gray-300 transition-transform"
                                        :class="{ 'rotate-180': expandedDates[dateGroup.date] }"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            <!-- Payout Items Table -->
                            <transition name="expand">
                                <div v-if="expandedDates[dateGroup.date]" class="bg-white">
                                    <!-- Table Header -->
                                    <div class="hidden md:grid md:grid-cols-12 gap-1 px-4 py-1.5 bg-gray-100 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                        <div class="col-span-3">Name</div>
                                        <div class="col-span-2">Agency</div>
                                        <div class="col-span-2">Task</div>
                                        <div class="col-span-1">Type</div>
                                        <div class="col-span-2">Completed</div>
                                        <div class="col-span-2 text-right">Amount</div>
                                    </div>

                                    <!-- Table Body -->
                                    <div>
                                        <div
                                            v-for="(payout, index) in dateGroup.payouts"
                                            :key="payout.id"
                                            class="px-4 py-2"
                                            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'"
                                        >
                                            <!-- Desktop Row -->
                                            <div class="hidden md:grid md:grid-cols-12 gap-1 items-center text-sm">
                                                <div class="col-span-3 truncate text-gray-900">{{ payout.payable?.name || 'Assignment' }}</div>
                                                <div class="col-span-2 truncate text-gray-600">{{ payout.payable?.agency || '-' }}</div>
                                                <div class="col-span-2 text-gray-600">{{ payout.payable_type === 'App\\Assignment' ? 'Coding' : 'Coder Help' }}</div>
                                                <div class="col-span-1 text-gray-600">{{ payout.payable?.document_type || '-' }}</div>
                                                <div class="col-span-2 text-gray-600 tabular-nums">{{ payout.payable?.coded_at || '-' }}</div>
                                                <div class="col-span-2 text-right font-medium text-mint-dark tabular-nums">${{ formatAmount(payout.amount) }}</div>
                                            </div>

                                            <!-- Mobile Card -->
                                            <div class="md:hidden space-y-1">
                                                <div class="flex justify-between items-start">
                                                    <div>
                                                        <div class="text-sm text-gray-900">{{ payout.payable?.name || 'Assignment' }}</div>
                                                        <div class="text-xs text-gray-500">{{ payout.payable?.agency || '-' }}</div>
                                                    </div>
                                                    <span class="text-sm font-medium text-mint-dark tabular-nums">${{ formatAmount(payout.amount) }}</span>
                                                </div>
                                                <div class="flex flex-wrap gap-x-3 text-xs text-gray-500">
                                                    <span>{{ payout.payable_type === 'App\\Assignment' ? 'Coding' : 'Coder Help' }}</span>
                                                    <span>{{ payout.payable?.document_type }}</span>
                                                    <span v-if="payout.payable?.coded_at">{{ payout.payable.coded_at }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <div class="w-14 h-14 mx-auto rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
            </div>
            <h3 class="text-base font-medium text-gray-900 mb-1">No payouts found</h3>
            <p class="text-sm text-gray-500">Your payout history will appear here</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PayoutHistory',

    props: {
        userId: {
            type: [Number, String],
            required: true
        }
    },

    data() {
        return {
            payouts: [],
            loading: false,
            selectedYear: new Date().getFullYear(),
            availableYears: [],
            ytdTotal: '0.00',
            yearlyTotals: {},
            monthlyTotals: {},
            expandedMonths: {},
            expandedDates: {},
            currentYear: new Date().getFullYear()
        };
    },

    computed: {
        hasData() {
            return this.payouts.length > 0;
        },

        sortedMonths() {
            const months = [];
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            const shortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            // Get months that have payouts
            Object.keys(this.monthlyTotals).forEach(monthNum => {
                const num = parseInt(monthNum);
                months.push({
                    number: num,
                    name: monthNames[num - 1],
                    shortName: shortNames[num - 1]
                });
            });

            // Sort by month descending (most recent first)
            return months.sort((a, b) => b.number - a.number);
        },

        groupedByDate() {
            const groups = {};
            this.payouts.forEach(payout => {
                const date = payout.paid_at.split('T')[0];
                if (!groups[date]) {
                    groups[date] = {
                        date,
                        payouts: [],
                        total: 0
                    };
                }
                groups[date].payouts.push(payout);
                groups[date].total += parseFloat(payout.amount);
            });
            return groups;
        }
    },

    mounted() {
        this.fetchPayouts();
    },

    methods: {
        async fetchPayouts() {
            this.loading = true;

            try {
                const response = await fetch(`/coders/${this.userId}/payouts?year=${this.selectedYear}`, {
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin'
                });

                if (!response.ok) throw new Error('Failed to fetch payouts');

                const data = await response.json();

                this.payouts = data.data;
                this.selectedYear = data.selected_year;
                this.availableYears = data.available_years;
                this.ytdTotal = data.ytd_total;
                this.yearlyTotals = data.yearly_totals;
                this.monthlyTotals = data.monthly_totals;

                // Auto-expand current month
                this.autoExpandCurrentMonth();
            } catch (error) {
                console.error('Error fetching payouts:', error);
            } finally {
                this.loading = false;
            }
        },

        autoExpandCurrentMonth() {
            // All groups collapsed by default - do nothing
        },

        selectYear(year) {
            if (this.selectedYear !== year) {
                this.selectedYear = year;
                this.expandedMonths = {};
                this.expandedDates = {};
                this.fetchPayouts();
            }
        },

        toggleMonth(monthNumber) {
            this.$set(this.expandedMonths, monthNumber, !this.expandedMonths[monthNumber]);
        },

        toggleDate(date) {
            this.$set(this.expandedDates, date, !this.expandedDates[date]);
        },

        getMonthPayoutCount(monthNumber) {
            return this.payouts.filter(p => {
                const payoutMonth = new Date(p.paid_at).getMonth() + 1;
                return payoutMonth === monthNumber;
            }).length;
        },

        getDateGroupsForMonth(monthNumber) {
            const groups = {};

            this.payouts.forEach(payout => {
                const payoutDate = new Date(payout.paid_at);
                if (payoutDate.getMonth() + 1 === monthNumber) {
                    const date = payout.paid_at.split('T')[0];
                    if (!groups[date]) {
                        groups[date] = {
                            date,
                            payouts: [],
                            total: 0
                        };
                    }
                    groups[date].payouts.push(payout);
                    groups[date].total += parseFloat(payout.amount);
                }
            });

            // Sort by date descending
            return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
        },

        formatDate(dateStr) {
            const date = new Date(dateStr);
            return this.formatDateShort(date);
        },

        formatDateShort(dateOrStr) {
            const date = typeof dateOrStr === 'string' ? new Date(dateOrStr) : dateOrStr;
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const y = date.getFullYear();
            return `${m}/${d}/${y}`;
        },

        formatAmount(amount) {
            return parseFloat(amount).toFixed(2);
        },

        exportToCsv() {
            const rows = [
                ['Paid Date', 'Coded Date', 'Type', 'Name', 'Document Type', 'Amount']
            ];

            this.payouts.forEach(payout => {
                rows.push([
                    payout.paid_at.split('T')[0],
                    payout.payable?.coded_at ? payout.payable.coded_at.split('T')[0] : '',
                    payout.payable_type === 'App\\Assignment' ? 'Coding' : 'Coder Help',
                    payout.payable?.name || 'Assignment',
                    payout.payable?.document_type || '',
                    payout.amount
                ]);
            });

            const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `payouts-${this.selectedYear}-${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
        }
    }
};
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
}

.expand-enter,
.expand-leave-to {
    opacity: 0;
    max-height: 0;
}

.expand-enter-to,
.expand-leave {
    opacity: 1;
    max-height: 2000px;
}
</style>
